// InvoicePrint — the printable invoice, and the thing "Export PDF" actually is.
//
// There is no server-rendered invoice PDF. The only PDF this application
// produces is a paystub (GET /api/time-entries/paystub/:userId), and
// moneyEndpointsContract.test.js pins that, so a screen that offered
// "Download PDF" for an invoice would be offering a request nobody answers.
// The classic Billing screen's button was `window.print()` over a print
// stylesheet, and this is the same mechanism with one difference that matters:
// the document is ON THE PAGE, not hidden behind `@media print`. A layout only
// the printer ever sees is a layout nobody has looked at, and this repo's
// worst defects this year were all layout ones that every unit test passed.
//
// ── Every figure here came off the wire ─────────────────────────────────────
//
// CLAUDE.md rule 4. `item.total` is the invoice_items.total column the server
// wrote; subtotal, discount, tax and total come from TotalsPanel, which
// DocumentEditor.test.js already pins as never computing one. Nothing here
// multiplies quantity by unit price — if the server did not send a line total,
// the cell says so rather than inventing one, and a note above the table says
// why. That case is not hypothetical: the column is NOT NULL in
// server/database.js, so a missing one means something upstream is wrong and
// hiding it behind a plausible number is exactly the failure this file must
// not have.

import React from 'react';
import { fmtMoney, fmtDate, fmtDateTime } from '../shared/format';
import { TotalsPanel } from '../shared/DocumentEditor';
import { clientNotes } from '../shared/clientNotes';
import './InvoicePrint.css';

/** A line total the SERVER sent, or null. Never derived. */
export function lineTotal(item) {
  if (!item) return null;
  const v = item.total;
  if (v === null || v === undefined || v === '') return null;
  return Number.isFinite(Number(v)) ? v : null;
}

/** How many lines are missing the server's total — 0 is the ordinary case. */
export function linesWithoutTotal(items) {
  return (Array.isArray(items) ? items : []).filter((it) => lineTotal(it) === null).length;
}

export function InvoicePrintDocument({ doc, settings = {} }) {
  if (!doc) return null;
  const items = Array.isArray(doc.items) ? doc.items : [];
  const missing = linesWithoutTotal(items);
  const photos = Array.isArray(doc.photos) ? doc.photos : [];
  const printedNotes = clientNotes(doc.notes);

  return (
    <article className="of-print" data-print="invoice">
      <header className="of-print__head">
        <div className="of-print__brand">
          {settings.brand_logo ? <img className="of-print__logo" src={settings.brand_logo} alt="" /> : null}
          <div>
            <h1 className="of-print__company">{settings.brand_name || 'Full Circle Property'}</h1>
            {settings.company_address ? <p>{settings.company_address}</p> : null}
            {settings.company_phone ? <p>{settings.company_phone}</p> : null}
            {settings.company_email ? <p>{settings.company_email}</p> : null}
          </div>
        </div>
        <div className="of-print__meta">
          <h2 className="of-print__kind">INVOICE</h2>
          <p className="of-print__number">{doc.invoice_number || `#${doc.id}`}</p>
          {doc.created_at ? <p>Date: {fmtDateTime(doc.created_at)}</p> : null}
          {doc.due_date ? <p>Due: {fmtDate(doc.due_date)}</p> : null}
          {doc.status ? <p>Status: {String(doc.status).toUpperCase()}</p> : null}
        </div>
      </header>

      <section className="of-print__billto">
        <h3>Bill to</h3>
        <p className="of-print__client">{doc.client_name || 'No client on this invoice'}</p>
        {doc.client_address ? <p>{doc.client_address}</p> : null}
        {doc.client_email ? <p>{doc.client_email}</p> : null}
        {doc.client_phone ? <p>{doc.client_phone}</p> : null}
      </section>

      {missing > 0 ? (
        <p className="of-print__note" data-print-note="missing-totals">
          {missing} line{missing === 1 ? '' : 's'} below {missing === 1 ? 'has' : 'have'} no line total
          from the server, so {missing === 1 ? 'it is' : 'they are'} shown as &quot;—&quot;. This app does not
          multiply quantity by price to fill one in; open the invoice and save it so the server
          recalculates before sending this to a client.
        </p>
      ) : null}

      <div className="of-print__scroll">
        <table className="of-print__table">
          <caption className="of-print__caption">Invoice lines</caption>
          <thead>
            <tr>
              <th scope="col">Description</th>
              <th scope="col" className="of-print__num">Qty</th>
              <th scope="col" className="of-print__num">Price</th>
              <th scope="col" className="of-print__num">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.length ? items.map((it, i) => (
              <tr key={it.id || i}>
                <td>
                  {/* Its own element so a checklist keeps its line breaks on paper (owner, 2026-09-26). */}
                  <span className="of-print__desc">{it.description}</span>
                  {it.notes ? <span className="of-print__sub">{it.notes}</span> : null}
                </td>
                <td className="of-print__num">{it.quantity}{it.unit ? ` ${it.unit}` : ''}</td>
                <td className="of-print__num">{fmtMoney(it.unit_price)}</td>
                <td className="of-print__num">{fmtMoney(lineTotal(it))}</td>
              </tr>
            )) : (
              <tr><td colSpan={4}>No lines on this invoice.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="of-print__totals">
        <TotalsPanel doc={doc} kind="invoice" pending={false} />
      </div>

      {/* The client's notes, without the payment paths' bookkeeping lines —
          the same cut the public page, the email and the PDF make. */}
      {printedNotes ? (
        <section className="of-print__notes">
          <h3>Notes</h3>
          <p>{printedNotes}</p>
        </section>
      ) : null}

      {doc.payment_terms ? (
        <section className="of-print__notes">
          <h3>Payment terms</h3>
          <p>{doc.payment_terms}</p>
        </section>
      ) : null}

      {photos.length ? (
        <section className="of-print__photos">
          <h3>Job photos</h3>
          <div className="of-print__grid">
            {photos.map((p, i) => (
              <figure key={p.id || p.media_id || i}>
                <img src={p.file_path} alt={p.caption || 'Job photo'} />
                {p.caption ? <figcaption>{p.caption}</figcaption> : null}
              </figure>
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}

export default InvoicePrintDocument;
